/**
 * CalendarGrid - The scheduling table: employees (grouped by category) × days.
 *
 * Renders the sticky header, the category rows and each employee row with its
 * document-status indicator and weekly-hours badge, delegating each day cell to
 * CalendarCell. Holds no state; drag-over tracking and shift mutations are
 * reported upward to the orchestrator.
 */
import type { Employee, PlannedShift, WorksiteForPlanning, WeekCalendarResponse } from './types';
import { CalendarCell } from './CalendarCell';

interface CalendarGridProps {
  weekDays: WeekCalendarResponse['days'];
  employeesByCategory: Record<string, Employee[]>;
  draggedWorksite: WorksiteForPlanning | null;
  dragOverCell: { empId: number; date: string } | null;
  getShiftsForCell: (employeeId: number, date: string) => PlannedShift[];
  onCellDragEnter: (empId: number, date: string) => void;
  onCellDragLeave: () => void;
  onDrop: (employeeId: number, date: string) => void;
  onDeleteShift: (shiftId: number) => void;
}

function nombreDelDia(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('es-ES', { weekday: 'short' });
}

export function CalendarGrid({
  weekDays,
  employeesByCategory,
  draggedWorksite,
  dragOverCell,
  getShiftsForCell,
  onCellDragEnter,
  onCellDragLeave,
  onDrop,
  onDeleteShift,
}: CalendarGridProps) {
  return (
    <div className="flex-1 bg-white rounded-lg border border-line overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          {/* Header */}
          <thead>
            <tr className="bg-surface-2">
              <th className="w-48 p-3 text-left text-sm font-semibold text-ink-2 border-b border-r border-line sticky left-0 bg-surface-2 z-10">
                Empleado
              </th>
              {/* `days` del backend es una lista de FECHAS, no de objetos:
                  no hay `day` ni `day.day_name`. El nombre del día se
                  calcula aquí. */}
              {weekDays.map((day) => (
                <th
                  key={day}
                  className="min-w-[120px] p-3 text-center text-sm font-semibold text-ink-2 border-b border-line"
                >
                  <div className="capitalize">{nombreDelDia(day)}</div>
                  <div className="text-xs text-ink-2 font-normal">
                    {new Date(`${day}T00:00:00`).getDate()}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          {/* Body */}
          <tbody>
            {Object.entries(employeesByCategory).map(([category, emps]) => (
              <>
                {/* Category header */}
                <tr key={`cat-${category}`}>
                  <td
                    colSpan={8}
                    className="p-2 bg-surface-2 text-sm font-medium text-ink-2 border-b border-line"
                  >
                    {category}
                  </td>
                </tr>

                {/* Employees */}
                {emps.map((emp) => (
                  <tr key={emp.id} className="border-b border-line">
                    {/* Employee cell */}
                    <td className="p-2 border-r border-line sticky left-0 bg-white z-10">
                      <div className="flex items-center gap-2">
                        {/* Estado documental PRL: sólo se pinta cuando hay
                            dato real. Antes un 'GREEN' inventado ponía el
                            punto verde a todo el mundo aunque el radar PRL
                            tuviera documentos bloqueantes caducados
                            (ronda 3, r2b21[8]). */}
                        {emp.document_status && (
                          <div
                            className={`w-2 h-2 rounded-full ${
                              emp.document_status === 'GREEN'
                                ? 'bg-brand'
                                : emp.document_status === 'YELLOW'
                                ? 'bg-warn' /* r2b21[4]: bg-warn-soft0 no existía en el design system — el punto salía invisible */
                                : 'bg-danger'
                            }`}
                            title={`Estado documental: ${emp.document_status}`}
                          />
                        )}
                        <div>
                          <p className="font-medium text-sm text-ink">
                            {emp.first_name} {emp.last_name}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-ink-2">
                            {/* Horas asignadas en la semana VISIBLE (suma de
                                los turnos del calendario), no las de contrato:
                                el «⚠️ HE» sólo salta cuando hay horas extra
                                de verdad asignadas. */}
                            {emp.weekly_hours_assigned != null && (
                              <>
                                <span>{emp.weekly_hours_assigned}h/sem</span>
                                {emp.weekly_hours_assigned >= 40 && (
                                  <span className="text-signal">⚠️ HE</span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Day cells */}
                    {weekDays.map((day) => {
                      const isVacation = emp.vacation_dates.includes(day);
                      const cellShifts = getShiftsForCell(emp.id, day);
                      const isDragOver =
                        dragOverCell?.empId === emp.id &&
                        dragOverCell?.date === day;

                      return (
                        <td
                          key={`${emp.id}-${day}`}
                          className="p-0 border-r border-line"
                          onDragEnter={() =>
                            draggedWorksite &&
                            !isVacation &&
                            onCellDragEnter(emp.id, day)
                          }
                          onDragLeave={onCellDragLeave}
                        >
                          <CalendarCell
                            employee={emp}
                            date={day}
                            shifts={cellShifts}
                            isDraggingOver={isDragOver}
                            isVacation={isVacation}
                            onDrop={onDrop}
                            onDeleteShift={onDeleteShift}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
